// The Space Resources card's rules — what a Space is bound to (its websites, business data, brand and social
// profiles), as symphony-bff's ListSpaceBindings answers it, turned into the groups and rows the card draws.
// Pure and import-free so it is unit-tested (`test/space-resources.test.ts`); the SAME rule, under the same
// fixture and the same assertions, lives on both phones (iOS `SpaceResources.swift`, Android
// `SpaceResources.kt`) — change one, change all three.
//
// orion-web's client bundle must not import common-lib, so the wire shape below is a client-local copy of
// symphony-bff's `SpaceResource` / `ListSpaceBindingsResponse`, exactly as `/api/space-bindings` passes it
// through. Enum values arrive as their proto names; one this build has never heard of is DROPPED, never
// guessed at — the card lists what it can describe and nothing else.

export type SpaceResourceKind = 'WEBSITE' | 'BUSINESS_DATA' | 'BRAND' | 'SOCIAL_PROFILE';
/**
 * CONFIRM / DISMISS answer a suggestion (a row in state CANDIDATE): the Space header asks "Is this the Space's
 * website?" and the Manage dialog lists every suggestion (`lib/presence.ts`). CONNECT turns a LINK_ONLY row into a
 * connected one, through the Space's chat. None of the three is a row-menu item. The regenerated BFF client may not
 * carry them yet; symphony-bff ships them under exactly these names.
 */
export type SpaceResourceAction = 'OPEN' | 'RECONNECT' | 'SET_MAIN' | 'MOVE_TO_SPACE' | 'UNBIND' | 'CONFIRM' | 'DISMISS' | 'CONNECT';

export interface SpaceResource {
  bindingId?: string;
  kind?: string;
  provider?: string;
  displayName?: string;
  loginLabel?: string;
  main?: boolean;
  purpose?: string;
  state?: string;
  openUrl?: string;
  actions?: string[];
  externalScopeId?: string;
  builtBySymphony?: boolean;
  screenshotUrl?: string;
}

export interface SpaceBindingsResponse {
  resources?: SpaceResource[];
  declinedKinds?: string[];
}

export interface I18nCopy {
  key: string;
  values?: Record<string, string>;
}

export type StateBadgeTone = 'warning' | 'critical' | 'quiet';

export interface SpaceResourceRow {
  bindingId: string;
  kind: SpaceResourceKind;
  provider: string;
  /** `display_name`, falling back to `external_scope_id`. */
  name: string;
  /** The "Main" badge's key when the row is its kind's main resource, else undefined. */
  mainBadgeKey?: string;
  /** The secondary line's parts, in order (a non-main website's purpose, then `via {login}`). Empty = no line. */
  secondary: I18nCopy[];
  /** The state badge, only when the resource is not plainly CONNECTED. */
  stateBadge?: { key: string; tone: StateBadgeTone };
  /** The row menu, in the fixed order below, holding only what the server offered for this row. */
  menu: { action: SpaceResourceAction; key: string }[];
  openUrl: string;
}

export interface SpaceResourceGroup {
  kind: SpaceResourceKind;
  labelKey: string;
  rows: SpaceResourceRow[];
  /** The kind was declined and has no rows: the group shows one quiet `workspace.resources.declined` line. */
  declinedKey?: string;
}

const K = 'workspace.resources.';

/** Group order on all three surfaces. */
const KIND_ORDER: { kind: SpaceResourceKind; labelKey: string }[] = [
  { kind: 'WEBSITE', labelKey: `${K}kindWebsite` },
  { kind: 'BUSINESS_DATA', labelKey: `${K}kindBusinessData` },
  { kind: 'BRAND', labelKey: `${K}kindBrand` },
  { kind: 'SOCIAL_PROFILE', labelKey: `${K}kindSocialProfile` },
];

/** Menu order on all three surfaces. SET_PURPOSE is deliberately absent: agents set it, v1 menus do not. */
const MENU_ORDER: { action: SpaceResourceAction; key: string }[] = [
  { action: 'OPEN', key: `${K}actionOpen` },
  { action: 'RECONNECT', key: `${K}actionReconnect` },
  { action: 'SET_MAIN', key: `${K}actionSetMain` },
  { action: 'MOVE_TO_SPACE', key: `${K}actionMove` },
  { action: 'UNBIND', key: `${K}actionUnbind` },
];

const PURPOSE_KEYS: Record<string, string> = {
  DESIGN_TEST: `${K}purposeDesignTest`,
  LANDING_PAGE: `${K}purposeLandingPage`,
  OTHER_PURPOSE: `${K}purposeOther`,
};

/** CONNECTED (and CANDIDATE, which never reaches a row) carry no badge. An absent state is proto3's
 *  UNKNOWN_STATE default, so it reads as unknown, never as connected. */
const STATE_BADGES: Record<string, { key: string; tone: StateBadgeTone }> = {
  // LINK_ONLY (a public link Symphony cannot act on) is a fact, not a problem: no badge, and no attention dot.
  BROKEN: { key: `${K}stateBroken`, tone: 'warning' },
  UNPUBLISHED: { key: `${K}stateUnpublished`, tone: 'quiet' },
  NOT_OWNED: { key: `${K}stateNotOwned`, tone: 'critical' },
  UNKNOWN_STATE: { key: `${K}stateUnknown`, tone: 'quiet' },
};

/** One resource as a row (name, Main badge, secondary line, state badge, menu) — the card's rows and the Manage dialog's. */
export function spaceResourceRow(resource: SpaceResource, kind: SpaceResourceKind): SpaceResourceRow {
  const main = resource.main === true;
  const openUrl = (resource.openUrl ?? '').trim();
  const offered = new Set(resource.actions ?? []);
  const secondary: I18nCopy[] = [];
  const purposeKey = kind === 'WEBSITE' && !main ? PURPOSE_KEYS[resource.purpose ?? ''] : undefined;
  if (purposeKey) secondary.push({ key: purposeKey });
  const login = (resource.loginLabel ?? '').trim();
  if (login) secondary.push({ key: `${K}via`, values: { login } });
  const state = resource.state || 'UNKNOWN_STATE';
  return {
    bindingId: resource.bindingId ?? '',
    kind,
    provider: resource.provider ?? '',
    name: (resource.displayName ?? '').trim() || (resource.externalScopeId ?? '').trim(),
    mainBadgeKey: main ? `${K}main` : undefined,
    secondary,
    stateBadge: STATE_BADGES[state],
    menu: MENU_ORDER.filter(({ action }) => {
      if (!offered.has(action)) return false;
      // Defensive against the server: a main row cannot be made main, and Open needs somewhere to go.
      if (action === 'SET_MAIN' && main) return false;
      if (action === 'OPEN' && !openUrl) return false;
      return true;
    }),
    openUrl,
  };
}

/**
 * The card's groups, in order. An empty array means the empty state (`workspace.resources.empty`): no row
 * to show and no kind declined. A kind with neither rows nor a decline is left out; a declined kind with no
 * rows shows its group with the quiet declined line. CANDIDATE rows and unknown kinds are dropped; within a
 * group the main row comes first, the rest keep the server's order.
 */
export function spaceResourceGroups(response: SpaceBindingsResponse | undefined): SpaceResourceGroup[] {
  const resources = (response?.resources ?? []).filter((r) => r.state !== 'CANDIDATE');
  const declined = new Set(response?.declinedKinds ?? []);
  const groups: SpaceResourceGroup[] = [];
  for (const { kind, labelKey } of KIND_ORDER) {
    const ofKind = resources.filter((r) => r.kind === kind);
    const rows = [...ofKind.filter((r) => r.main === true), ...ofKind.filter((r) => r.main !== true)].map((r) => spaceResourceRow(r, kind));
    if (rows.length > 0) groups.push({ kind, labelKey, rows });
    else if (declined.has(kind)) groups.push({ kind, labelKey, rows: [], declinedKey: `${K}declined` });
  }
  return groups;
}

/** What to tell the reader after an UpdateSpaceBinding. `null` = it landed (UPDATED / UNCHANGED): refresh. */
export function spaceBindingUpdateError(outcome: string | undefined): string | null {
  switch (outcome) {
    case 'UPDATED':
    case 'UNCHANGED':
      return null;
    case 'WEBSITE_IN_ANOTHER_SPACE':
      return `${K}errorWebsiteInAnotherSpace`;
    case 'SITE_NOT_OWNED':
      return `${K}errorNotOwned`;
    default:
      return `${K}errorGeneric`;
  }
}

/** The Spaces a resource can move to: every one of the account's Spaces except the one it is in. */
export function moveTargets<W extends { id: string }>(workspaces: W[], currentWorkspaceId: string): W[] {
  return workspaces.filter((w) => w.id && w.id !== currentWorkspaceId);
}

const PROVIDER_NAMES: Record<string, string> = {
  WIX: 'Wix site',
  SHOPIFY: 'Shopify',
  WORDPRESS: 'WordPress',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  GOOGLE_BUSINESS: 'Google Business Profile',
  WIXEL: 'Wixel',
  URL: 'website',
  TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn',
  X: 'X',
  YOUTUBE: 'YouTube',
  PINTEREST: 'Pinterest',
  YELP: 'Yelp',
  THREADS: 'Threads',
  ETSY: 'Etsy',
};

/** The provider as a person names it, for the Reconnect chat seed. */
export function providerName(provider: string): string {
  return PROVIDER_NAMES[provider] ?? 'account';
}

/**
 * Reconnect on web opens a chat seeded to reconnect, in the Space's context — the agent runs the provider's
 * connect from there. The seed is a message TO the agent, and the spec carries no catalog key for it, so it
 * is the same English sentence on all three surfaces.
 */
export function reconnectPrompt(row: Pick<SpaceResourceRow, 'provider'>): string {
  return `Reconnect my ${providerName(row.provider)} for this Space`;
}

/** The hidden context a Reconnect chat carries, so the agent works in this Space on this binding. */
export function spaceResourceChatContext(
  workspaceId: string,
  workspaceName: string,
  row: Pick<SpaceResourceRow, 'provider' | 'name' | 'bindingId'>,
): string {
  return (
    `The user is asking about a resource of their "${workspaceName}" Space (workspace id "${workspaceId}"): ` +
    `their ${providerName(row.provider)} "${row.name}" (binding id "${row.bindingId}"), whose login needs reconnecting. ` +
    `Work in this Space. Don't ask which Space.`
  );
}
